import { loadUsageAnalyticsConfig, recordUsageAnalyticsBatch, type UsageAnalyticsEvent } from "@/pages/administration/api/administration-api";

const tickIntervalMS = 5_000;
const flushIntervalMS = 30_000;
const engagedWindowMS = 60_000;
const rageWindowMS = 2_000;
const nearMissDistancePX = 24;
const maxPendingEvents = 200;

type TargetDescriptor = {
    target_id: string;
    target_kind: NonNullable<UsageAnalyticsEvent["target_kind"]>;
    target_width?: UsageAnalyticsEvent["target_width"];
    target_height?: UsageAnalyticsEvent["target_height"];
};

let requestedUserID = "";
let requestedLocation = "";
let configuredUserID = "";
let enabled = false;
let listenersReady = false;
let currentPage = "";
let sessionID = createAnalyticsID("session");
let pendingEvents: UsageAnalyticsEvent[] = [];
let visibleMS = 0;
let engagedMS = 0;
let lastTickAt = Date.now();
let lastActivityAt = Date.now();
let tickTimer: number | null = null;
let flushTimer: number | null = null;
let flushInFlight: Promise<void> | null = null;
let rageState = { key: "", count: 0, lastAt: 0, emitted: false };

export function updateUsageAnalyticsContext(userID: string, location: string) {
    requestedUserID = userID;
    requestedLocation = location;

    if (!userID) {
        stopUsageAnalytics();
        configuredUserID = "";
        return;
    }

    if (configuredUserID === userID) {
        if (enabled) setUsageAnalyticsPage(location);
        return;
    }

    stopUsageAnalytics();
    configuredUserID = userID;
    sessionID = createAnalyticsID("session");
    void loadUsageAnalyticsConfig().then((config) => {
        if (requestedUserID !== userID) return;
        enabled = config.enabled;
        if (!enabled) return;
        initializeUsageAnalyticsListeners();
        startUsageAnalyticsTimers();
        setUsageAnalyticsPage(requestedLocation);
    }).catch(() => {
        enabled = false;
        if (requestedUserID === userID) configuredUserID = "";
    });
}

export function stopUsageAnalytics() {
    accumulatePageTime();
    queuePageTime();
    currentPage = "";
    enabled = false;
    if (tickTimer !== null) window.clearInterval(tickTimer);
    if (flushTimer !== null) window.clearInterval(flushTimer);
    tickTimer = null;
    flushTimer = null;
    void flushUsageAnalytics(true);
}

export function normalizeUsageAnalyticsPage(location: string) {
    const pathname = location.split(/[?#]/, 1)[0]?.replace(/\/+$/, "") || "/";
    const exactPages: Record<string, string> = {
        "/beds": "beds",
        "/events": "events",
        "/patients": "patients",
        "/report": "report",
        "/report/print/bed-board": "report.print.bed-board",
        "/report/print/status-report": "report.print.status-report",
        "/hap": "hap",
        "/statistics": "statistics",
        "/status": "status",
    };
    if (exactPages[pathname]) return exactPages[pathname];

    const manageMatch = pathname.match(/^\/manage\/(users|beds|ovd|settings|backup|reset|analytics)$/);
    if (manageMatch) return `manage.${manageMatch[1]}`;
    const statusMatch = pathname.match(/^\/status\/(connection|diagnostics|logs)$/);
    if (statusMatch) return `status.${statusMatch[1]}`;
    return null;
}

function setUsageAnalyticsPage(location: string) {
    const nextPage = normalizeUsageAnalyticsPage(location) ?? "";
    if (nextPage === currentPage) return;
    accumulatePageTime();
    queuePageTime();
    currentPage = nextPage;
    lastTickAt = Date.now();
    if (currentPage) {
        queueEvent({ type: "page_visit", page: currentPage, session_id: sessionID });
    }
    void flushUsageAnalytics();
}

function initializeUsageAnalyticsListeners() {
    if (listenersReady) return;
    listenersReady = true;
    window.addEventListener("pointerdown", recordUserActivity, { passive: true });
    window.addEventListener("keydown", recordUserActivity);
    window.addEventListener("touchstart", recordUserActivity, { passive: true });
    window.addEventListener("focus", recordUserActivity);
    window.addEventListener("blur", handleVisibilityChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("click", handleClick, true);
    window.addEventListener("pagehide", handlePageHide);
}

function startUsageAnalyticsTimers() {
    lastTickAt = Date.now();
    lastActivityAt = lastTickAt;
    if (tickTimer === null) tickTimer = window.setInterval(() => accumulatePageTime(), tickIntervalMS);
    if (flushTimer === null) flushTimer = window.setInterval(() => {
        accumulatePageTime();
        queuePageTime();
        void flushUsageAnalytics();
    }, flushIntervalMS);
}

function recordUserActivity() {
    const now = Date.now();
    accumulatePageTime(now);
    lastActivityAt = now;
}

function handleVisibilityChange() {
    accumulatePageTime();
    if (document.visibilityState !== "visible" || !document.hasFocus()) {
        queuePageTime();
        void flushUsageAnalytics();
    }
}

function handlePageHide() {
    accumulatePageTime();
    queuePageTime();
    void flushUsageAnalytics(true);
}

function accumulatePageTime(now = Date.now()) {
    const elapsed = Math.max(0, Math.min(now - lastTickAt, tickIntervalMS * 2));
    lastTickAt = now;
    if (!enabled || !currentPage || document.visibilityState !== "visible" || !document.hasFocus()) return;
    visibleMS += elapsed;
    if (now - lastActivityAt <= engagedWindowMS) engagedMS += elapsed;
}

function queuePageTime() {
    if (!currentPage || visibleMS <= 0) {
        visibleMS = 0;
        engagedMS = 0;
        return;
    }
    queueEvent({ type: "page_time", page: currentPage, session_id: sessionID, visible_ms: Math.round(visibleMS), engaged_ms: Math.round(Math.min(engagedMS, visibleMS)) });
    visibleMS = 0;
    engagedMS = 0;
}

function handleClick(event: MouseEvent) {
    if (!enabled || !currentPage || !event.isTrusted) return;
    recordUserActivity();
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const interactive = target.closest<HTMLElement>("[data-analytics-id],button,a[href],input,select,textarea,[role='button']");
    const coordinates = clickCoordinates(event);
    if (interactive) {
        const descriptor = describeTarget(interactive);
        queueEvent({ type: "click", page: currentPage, session_id: sessionID, ...coordinates, ...descriptor });
        trackRageClick(descriptor.target_id, coordinates, descriptor);
    } else {
        queueEvent({ type: "dead_click", page: currentPage, session_id: sessionID, ...coordinates, target_kind: "other" });
        const nearTarget = findNearTarget(event.clientX, event.clientY);
        if (nearTarget) queueEvent({ type: "near_miss", page: currentPage, session_id: sessionID, ...coordinates, ...nearTarget });
        trackRageClick(`area.${coordinates.x}.${coordinates.y}`, coordinates, { target_id: `area.${coordinates.x}.${coordinates.y}`, target_kind: "other" });
    }
}

function trackRageClick(targetID: string, coordinates: ReturnType<typeof clickCoordinates>, descriptor: TargetDescriptor) {
    const now = Date.now();
    const key = `${currentPage}:${targetID}:${coordinates.x}:${coordinates.y}`;
    if (rageState.key === key && now - rageState.lastAt <= rageWindowMS) {
        rageState.count++;
    } else {
        rageState = { key, count: 1, lastAt: now, emitted: false };
    }
    rageState.lastAt = now;
    if (rageState.count >= 3 && !rageState.emitted) {
        rageState.emitted = true;
        queueEvent({ type: "rage_click", page: currentPage, session_id: sessionID, ...coordinates, ...descriptor });
    }
}

function findNearTarget(x: number, y: number) {
    let nearest: { distance: number; element: HTMLElement } | null = null;
    for (const element of document.querySelectorAll<HTMLElement>("[data-analytics-id],a[href]")) {
        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        const dx = Math.max(rect.left - x, 0, x - rect.right);
        const dy = Math.max(rect.top - y, 0, y - rect.bottom);
        const distance = Math.hypot(dx, dy);
        if (distance <= nearMissDistancePX && (!nearest || distance < nearest.distance)) nearest = { distance, element };
    }
    return nearest ? describeTarget(nearest.element) : null;
}

function describeTarget(element: HTMLElement) {
    const configuredID = sanitizeAnalyticsID(element.dataset.analyticsId ?? "");
    let targetID = configuredID;
    let targetKind: UsageAnalyticsEvent["target_kind"] = "other";
    if (element.matches("a[href]")) {
        targetKind = "link";
        if (!targetID) {
            const href = element.getAttribute("href") ?? "";
            const linkedPage = normalizeUsageAnalyticsPage(href);
            targetID = linkedPage ? `link.${linkedPage}` : "other.generic";
        }
    } else if (element.matches("button,[role='button']")) {
        targetKind = "button";
        targetID ||= "button.generic";
    } else if (element.matches("input,select,textarea")) {
        targetKind = "control";
        targetID ||= "control.generic";
    }
    targetID ||= "other.generic";
    const rect = element.getBoundingClientRect();
    return { target_id: targetID, target_kind: targetKind, target_width: widthBucket(rect.width), target_height: heightBucket(rect.height) } satisfies TargetDescriptor;
}

function clickCoordinates(event: Pick<MouseEvent, "clientX" | "clientY">) {
    const width = Math.max(window.innerWidth, 1);
    const height = Math.max(window.innerHeight, 1);
    return {
        viewport: viewportBucket(width),
        x: Math.min(19, Math.max(0, Math.floor((event.clientX / width) * 20))),
        y: Math.min(19, Math.max(0, Math.floor((event.clientY / height) * 20))),
    } as const;
}

function queueEvent(event: UsageAnalyticsEvent) {
    pendingEvents.push(event);
    if (pendingEvents.length >= maxPendingEvents) void flushUsageAnalytics();
}

async function flushUsageAnalytics(keepalive = false) {
    if (flushInFlight || pendingEvents.length === 0 || !configuredUserID) return flushInFlight ?? Promise.resolve();
    flushInFlight = (async () => {
        while (pendingEvents.length > 0) {
            const events = pendingEvents.splice(0, maxPendingEvents);
            try {
                await recordUsageAnalyticsBatch({ batch_id: createAnalyticsID("batch"), events }, keepalive);
            } catch {
                if (!keepalive) pendingEvents = [...events, ...pendingEvents].slice(0, maxPendingEvents);
                break;
            }
        }
    })().finally(() => {
        flushInFlight = null;
    });
    return flushInFlight;
}

function viewportBucket(width: number): "small" | "medium" | "large" {
    if (width < 768) return "small";
    if (width < 1280) return "medium";
    return "large";
}

function widthBucket(value: number): "small" | "medium" | "large" {
    if (value < 80) return "small";
    if (value < 160) return "medium";
    return "large";
}

function heightBucket(value: number): "small" | "medium" | "large" {
    if (value < 36) return "small";
    if (value < 52) return "medium";
    return "large";
}

function sanitizeAnalyticsID(value: string) {
    const normalized = value.trim().toLowerCase();
    return /^[a-z0-9][a-z0-9._:-]{0,79}$/.test(normalized) ? normalized : "";
}

function createAnalyticsID(prefix: string) {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
